import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DouyinDistributionType, useDouyinProfileAudienceQuery } from '@anytag/graphql';
import { ChartsContainer, SplitButton } from '@anytag/shared-atoms';
import { HAS_NO_ESTIMATED_TEXT } from '@anytag/shared-constants';
import { AgeBreakdownChart, AreaBreakdownChart, GenderBreakdownChart } from '@anytag/shared-molecules';
import { useInfluencerProfileOpacityContent } from '../../hooks';
import { InfluencerSelectedAccountInfo } from '../../types';
import { InfluencerProfileAudienceWrapper } from '../shared';

export interface InfluencerDouyinProfileAudienceProps {
  influencerId: number;
  socialAccount: InfluencerSelectedAccountInfo;
}

export const InfluencerDouyinProfileAudience = ({
  influencerId,
  socialAccount
}: InfluencerDouyinProfileAudienceProps) => {
  const { t } = useTranslation();
  const [distributionType, setDistributionType] = useState<DouyinDistributionType>(DouyinDistributionType.ANYONE);
  const { opacityContent, hasEstimatedDemographics } = useInfluencerProfileOpacityContent({
    influencerId,
    socialAccount
  });
  const { data, loading } = useDouyinProfileAudienceQuery({
    variables: { pk: influencerId, socialAccountId: socialAccount.id, distributionType }
  });

  const chartOpacityContent = hasEstimatedDemographics ? opacityContent : { wrapperText: HAS_NO_ESTIMATED_TEXT };

  return (
    <InfluencerProfileAudienceWrapper
      loading={loading}
      data={data?.douyinProfileAudience}
      hasEstimatedDemographics={hasEstimatedDemographics}
      headerChildren={
        <SplitButton
          options={[
            {
              title: t('Anyone'),
              variant: 'outline',
              onClick: () => setDistributionType(DouyinDistributionType.ANYONE),
              isActive: distributionType === DouyinDistributionType.ANYONE
            },
            {
              title: t('Followers'),
              variant: 'outline',
              onClick: () => setDistributionType(DouyinDistributionType.FOLLOWER),
              isActive: distributionType === DouyinDistributionType.FOLLOWER
            }
          ]}
          className="ml-auto min-w-22.5"
        />
      }
    >
      {({ data: { maleRate, femaleRate, ageRates, regionRates } }) => (
        <ChartsContainer
          css={{ height: '385px' }}
          elements={[
            <GenderBreakdownChart maleRate={maleRate} femaleRate={femaleRate} {...chartOpacityContent} />,
            <AgeBreakdownChart ageGroup={ageRates.ageGroup} totalRate={ageRates.rate} {...chartOpacityContent} />,
            <AreaBreakdownChart followerRegionRate={regionRates || []} />
          ]}
        />
      )}
    </InfluencerProfileAudienceWrapper>
  );
};
